import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Home, DollarSign, MapPin } from 'lucide-react';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const SCORE_BADGE = {
  green: 'bg-green-100 text-green-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  red: 'bg-red-100 text-red-700',
};

const REACTION_LABELS = {
  love: '❤️ Love it',
  'thumbs-up': '👍 Looks good',
  'thumbs-down': '👎 Not a fit',
};

export default function PublicHouseView() {
  const { token } = useParams();
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchShare = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`/api/share/${token}`);
        if (!response.ok) {
          throw new Error('This share link is no longer available.');
        }
        const data = await response.json();
        setShareData(data.shareData);
      } catch (err) {
        setError(err.message || 'Unable to load shared house.');
      } finally {
        setLoading(false);
      }
    };

    fetchShare();
  }, [token]);

  const reactionSummary = useMemo(() => {
    const summary = shareData?.reactions?.reduce((acc, reaction) => {
      if (!reaction.reaction) return acc;
      acc[reaction.reaction] = (acc[reaction.reaction] || 0) + 1;
      return acc;
    }, {}) || {};

    return Object.entries(summary).map(([key, count]) => ({
      key,
      label: REACTION_LABELS[key] || key,
      count,
    }));
  }, [shareData?.reactions]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
        <div className="flex items-center gap-3 text-lg">
          <Loader2 className="w-6 h-6 animate-spin" />
          Loading house details…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white px-4 text-center">
        <div className="max-w-md space-y-4">
          <h1 className="text-2xl font-bold">Link unavailable</h1>
          <p className="text-slate-200">{error}</p>
          <a
            href="https://avri.simplified-labs.com"
            className="inline-flex items-center justify-center bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold"
          >
            Visit AvRi
          </a>
        </div>
      </div>
    );
  }

  if (!shareData) {
    return null;
  }

  const scoreClass = SCORE_BADGE[shareData.score] || SCORE_BADGE.yellow;
  const analysis = shareData.analysis_data || {};

  return (
    <div className="min-h-screen bg-slate-900 text-white py-12 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        <header className="bg-slate-800/70 backdrop-blur rounded-3xl border border-slate-700 p-8 shadow-xl">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide bg-slate-700 text-slate-300">
                Shared by {shareData.created_by_name || 'a household member'}
              </div>
              <h1 className="mt-4 text-3xl md:text-4xl font-bold flex items-center gap-3">
                <Home className="w-8 h-8 text-indigo-400" />
                {shareData.address || 'Future Home'}
              </h1>
              <p className="mt-2 text-lg text-slate-200 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-indigo-400" />
                {shareData.url ? (
                  <a href={shareData.url} target="_blank" rel="noreferrer" className="underline hover:text-indigo-300">
                    View original listing
                  </a>
                ) : (
                  'Listing link not provided'
                )}
              </p>
            </div>
            <div className="text-right space-y-2">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${scoreClass}`}>
                Score: {shareData.score?.toUpperCase() || 'N/A'}
              </div>
              <p className="text-3xl font-bold">{shareData.price ? currency.format(shareData.price) : 'Price TBD'}</p>
            </div>
          </div>
        </header>

        <section className="grid gap-4 md:grid-cols-2">
          <div className="bg-slate-800/60 rounded-2xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-indigo-400" />
              Monthly Snapshot
            </h2>
            <ul className="space-y-2 text-sm text-slate-200">
              <li className="flex justify-between">
                <span>Total monthly cost</span>
                <strong>{analysis.totalMonthly ? currency.format(analysis.totalMonthly) : '—'}</strong>
              </li>
              <li className="flex justify-between">
                <span>Principal & Interest</span>
                <strong>{analysis.principalInterest ? currency.format(analysis.principalInterest) : '—'}</strong>
              </li>
              <li className="flex justify-between">
                <span>Taxes + Insurance</span>
                <strong>{analysis.monthlyTax && analysis.monthlyInsurance
                  ? currency.format(analysis.monthlyTax + analysis.monthlyInsurance)
                  : '—'}</strong>
              </li>
              <li className="flex justify-between">
                <span>HOA</span>
                <strong>{analysis.monthlyHOA ? currency.format(analysis.monthlyHOA) : '—'}</strong>
              </li>
              <li className="flex justify-between">
                <span>PMI</span>
                <strong>{analysis.monthlyPMI ? currency.format(analysis.monthlyPMI) : '—'}</strong>
              </li>
            </ul>
          </div>

          <div className="bg-slate-800/60 rounded-2xl p-6 border border-slate-700 space-y-3">
            <h2 className="text-xl font-semibold">Household Notes</h2>
            <p className="text-sm text-slate-200 whitespace-pre-line">
              {shareData.notes || 'This household has not added any notes yet.'}
            </p>

            {reactionSummary.length > 0 && (
              <div className="pt-3 border-t border-slate-700">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400 mb-2">Reactions</h3>
                <div className="grid gap-2 text-sm text-slate-200">
                  {shareData.reactions.map((reaction) => (
                    <div key={`${reaction.user_id}-${reaction.reaction}`} className="flex items-center gap-2">
                      <span>{REACTION_LABELS[reaction.reaction] || '🤔 Reviewing'}</span>
                      <span className="text-slate-400 text-xs">by {reaction.user_name || 'Household member'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <footer className="text-center text-sm text-slate-400">
          Powered by AvRi • Making homebuying honest and transparent
        </footer>
      </div>
    </div>
  );
}
